import { FC, forwardRef, ReactElement } from "react";
import {
  Box,
  Button,
  Dialog,
  FormLabel,
  Slide,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useForm, Controller } from "react-hook-form";

import TextInput from "../textfields";
import { modal_close } from "@/constants";
import { useBookADemoMutation } from "@/services";
import { ValidationPatterns } from "@/utils/form-validation";
import { useSnackbar } from "@/hooks";
import { tokens } from "@/tokens";

type FormData = {
  name: string;
  email: string;
  companyName: string;
  firmType: "Importer" | "Service Provider";
};

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CardGradient = styled(Box)({
  width: "100%",
  height: "165px",
  background:
    "linear-gradient(180deg, #697A7B 0%, rgba(205, 211, 211, 0.00) 58%)",
  position: "absolute",
  top: 0,
  left: 0,
});

const StyledCloseButton = styled(Button)({
  padding: 0,
  minWidth: "initial",
  width: "fit-content",
  position: "absolute",
  top: 16,
  right: 24,
  borderRadius: "5px",
  zIndex: 2,
});

const StyledButton = styled(Button)({
  padding: "12px 35px",
  border: `1px solid ${tokens.border.default}`,
  borderRadius: tokens.radius.md,
  fontSize: tokens.fontSize.lg,
  fontWeight: tokens.fontWeight.semibold,
  color: tokens.brand.primary,
  whiteSpace: "nowrap",

  "&.active": {
    background: "rgba(5, 34, 35, 0.12)",
    borderColor: tokens.brand.primary,
  },
});

const StyledLabel = styled(FormLabel)({
  color: tokens.text.primary,
  fontSize: tokens.fontSize.lg,
  fontWeight: tokens.fontWeight.semibold,
  marginBottom: 8,
});

const StyledInput = styled(TextInput)(({ error }: { error?: boolean }) => ({
  marginBottom: "14px",
  marginTop: "6px",
  background: tokens.bg.surface,
  "& fieldset": {
    border: `1px solid ${error ? "#d32f2f" : tokens.border.default}`,
    borderRadius: tokens.radius.md,
  },
  "& input::placeholder": {
    color: "#ADAFB9",
  },
  "& .MuiFormHelperText-root": {
    color: "#d32f2f",
    marginLeft: 0,
    marginTop: 0
  }
}));

const SubmitButton = styled(Button)({
  background: tokens.brand.primary,
  color: tokens.bg.surface,
  fontSize: tokens.fontSize.xl,
  fontWeight: tokens.fontWeight.semibold,
  borderRadius: tokens.radius.md,
  padding: "12px 16px",
  marginTop: 14,

  "&:hover": {
    background: tokens.brand.primaryHover,
  },

  "&:disabled": {
    background: "#ADAFB9",
    color: tokens.bg.surface
  }
});

const ContactModal: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }): ReactElement => {
  const { show: showSnackBar } = useSnackbar();
  const [bookADemo, { isLoading }] = useBookADemoMutation();

  const { control, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: "",
      email: "",
      companyName: "",
      firmType: "Importer",
    },
  });

  const firmType = watch("firmType");

  const onSubmit = async (data: FormData) => {
    try {
      await bookADemo(data).unwrap();
      showSnackBar("Thank you for your interest in Riffs!",
        `We're currently experiencing high demand and scheduling demos to \nensure each client gets a tailored experience.Our team will be in \ntouch shortly to arrange your personalized walkthrough.`,
        "success",
        8000);
      reset();
      onClose();
    } catch (error) {
      showSnackBar("Failed to submit the form. Please try again.", "", "error");
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      sx={{
        "& .MuiPaper-root": {
          maxWidth: 470,
          position: "relative",
          padding: {
            xs: "1rem",
            sm: "28px 24px",
          },
          margin: {
            xs: "0",
            sm: 2,
          },
        },
      }}
    >
      <CardGradient />
      <StyledCloseButton onClick={onClose}>
        <Box component="img" src={modal_close} sx={{ width: 26, height: 26 }} />
      </StyledCloseButton>
      <Typography fontWeight={tokens.fontWeight.semibold} fontSize={28} zIndex={1}>
        ☎️ Contact Us
      </Typography>
      <Typography color="#8C8E9C" fontWeight={tokens.fontWeight.normal} fontSize={tokens.fontSize.base} mb={2.5} mt={1} width={"80%"}>
        Tell us about your firm or company and our team will reach out to set up
        a call.
      </Typography>
      <StyledLabel>Firm Type*</StyledLabel>
      <Stack direction="row" spacing={1.5} mb="14px">
        <StyledButton
          onClick={() => setValue("firmType", "Importer")}
          className={firmType === "Importer" ? "active" : ""}
          type="button"
        >
          Importer
        </StyledButton>
        <StyledButton
          onClick={() => setValue("firmType", "Service Provider")}
          className={firmType === "Service Provider" ? "active" : ""}
          type="button"
        >
          Service Provider
        </StyledButton>
      </Stack>

      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledLabel htmlFor="name">Your Name</StyledLabel>
        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, ...rest } }) => (
            <StyledInput
              {...rest}
              variant="outlined"
              id="name"
              placeholder="eg Alex Sam"
              type="text"
              size="medium"
              color="secondary"
              error={!!errors.name}
              helperText={errors.name?.message}
              handleChangeValue={(value) => onChange(value)}
            />
          )}
        />

        <StyledLabel htmlFor="email">Email*</StyledLabel>
        <Controller
          name="email"
          control={control}
          rules={{
            required: "Email is required",
            pattern: {
              value: ValidationPatterns.email,
              message: "Invalid email format"
            }
          }}
          render={({ field: { onChange, ...rest } }) => (
            <StyledInput
              {...rest}
              variant="outlined"
              id="email"
              placeholder="hi@example.com"
              type="email"
              size="medium"
              color="secondary"
              error={!!errors.email}
              helperText={errors.email?.message}
              handleChangeValue={(value) => onChange(value)}
              required
            />
          )}
        />

        <StyledLabel htmlFor="companyName">Company Name</StyledLabel>
        <Controller
          name="companyName"
          control={control}
          render={({ field: { onChange, ...rest } }) => (
            <StyledInput
              {...rest}
              variant="outlined"
              id="companyName"
              placeholder="eg Broadgage"
              type="text"
              size="medium"
              color="secondary"
              error={!!errors.companyName}
              helperText={errors.companyName?.message}
              handleChangeValue={(value) => onChange(value)}
            />
          )}
        />

        <SubmitButton
          type="submit"
          fullWidth
          disabled={isLoading}
        >
          {isLoading ? "Submitting..." : "Submit"}
        </SubmitButton>
      </form>
    </Dialog>
  );
};

export default ContactModal;
